import React, { memo, useState } from "react";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Typography,
} from "@mui/material";

const TONE_STYLES = {
  critical: {
    bar: "#ef4444",
    badgeBg: "#fef2f2",
    badgeColor: "#dc2626",
    personColor: "#dc2626",
    alertBg: "#fff",
  },
  warning: {
    bar: "#f59e0b",
    badgeBg: "#fffbeb",
    badgeColor: "#d97706",
    personColor: "#dc2626",
    alertBg: "#fff",
  },
};

const AttentionAlertItem = memo(({
  item,
  onFollowUp,
  onReassign,
  onPersonClick,
}) => {
  const tone = TONE_STYLES[item.tone] || TONE_STYLES.critical;

  return (
    <Alert
      className="dashboard-attention-alert"
      role="listitem"
      severity="error"
      icon={false}
      sx={{
        alignItems: "stretch",
        p: 0,
        borderRadius: 0,
        bgcolor: tone.alertBg,
        border: "none",
        borderBottom: "1px solid #f0f1f3",
        "&:last-child": { borderBottom: "none" },
        "& .MuiAlert-message": { p: 0, width: "100%" },
        "& .MuiAlert-action": { p: 0, m: 0, pr: 2, alignItems: "center" },
      }}
      action={
        <Box className="dashboard-attention-alert-actions">
          {/* Hidden for now
          <Button
            variant="contained"
            size="small"
            className="dashboard-attention-btn dashboard-attention-btn--primary"
            onClick={() => onFollowUp?.(item)}
          >
            Follow Up
          </Button>
          */}
          {item.showReassign !== false && (
            <Button
              variant="outlined"
              size="small"
              className="dashboard-attention-btn dashboard-attention-btn--secondary"
              onClick={() => onReassign?.(item)}
            >
              Reassign
            </Button>
          )}
        </Box>
      }
    >
      <Box className="dashboard-attention-alert-body">
        <Box
          className="dashboard-attention-alert-bar"
          sx={{ bgcolor: tone.bar }}
          aria-hidden="true"
        />
        <Box
          className="dashboard-attention-alert-content"
          sx={{ flexDirection: 'row', alignItems: 'flex-start', gap: 1.5 }}
        >
          <Chip
            label={item.statusLabel}
            size="small"
            className="dashboard-attention-badge"
            sx={{
              bgcolor: tone.badgeBg,
              color: tone.badgeColor,
              fontWeight: 700,
              fontSize: "0.68rem",
              letterSpacing: "0.04em",
              height: 22,
              borderRadius: "4px",
              mt: 0.2,
              "& .MuiChip-label": { px: 1 },
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0.25 }}>
            <Typography className="dashboard-attention-alert-title" component="p">
              {item.person && (
                <>
                  <button
                    type="button"
                    className="dashboard-attention-person-link"
                    style={{ color: tone.personColor }}
                    onClick={() => onPersonClick?.(item)}
                  >
                    {item.person}
                  </button>
                  {" — "}
                </>
              )}
              <span className="dashboard-attention-task">&quot;{item.task}&quot;</span>
              {" "}
              <span className="dashboard-attention-desc" style={{ fontSize: "0.85em", color: "#4b5563" }}>
                {item.message}
              </span>
            </Typography>
            {item.metadata?.length > 0 && (
              <Typography
                className="dashboard-attention-alert-meta"
                component="p"
                sx={{ fontSize: "85%" }}
              >
                {item.metadata.join(" · ")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Alert>
  );
});

AttentionAlertItem.displayName = "AttentionAlertItem";

const DashboardAttentionAlerts = ({
  items = [],
  totalCount,
  onFollowUp,
  onReassign,
  onPersonClick,
  onShowMore,
  isLoading = false,
}) => {
  const [showAll, setShowAll] = useState(false);

  if (isLoading && !items.length) {
    return (
      <section className="dashboard-attention-section">
        <Box className="dashboard-attention-card">
           <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>Loading alerts...</Typography>
        </Box>
      </section>
    );
  }

  if (!items.length) return null;

  const count = totalCount ?? items.length;
  const displayedItems = showAll ? items : items.slice(0, 3);
  const hasMore = items.length > 3;

  return (
    <section className="dashboard-attention-section">
      <Box className="dashboard-attention-card">
        <Box className="dashboard-attention-header">
          <Box className="dashboard-attention-header-title">
            <WarningAmberOutlinedIcon className="dashboard-attention-header-icon" />
            <Typography component="h2" variant="subtitle1">
              Needs Your Attention
            </Typography>
          </Box>
          <Chip
            label={`${count} items`}
            size="small"
            className="dashboard-attention-count"
            sx={{
              bgcolor: "#fef2f2",
              color: "#dc2626",
              fontWeight: 600,
              fontSize: "0.75rem",
              height: 24,
              borderRadius: "999px",
            }}
          />
        </Box>

        <Box className="dashboard-attention-list" role="list">
          {displayedItems.map((item) => (
            <AttentionAlertItem
              key={item.id}
              item={item}
              onFollowUp={onFollowUp}
              onReassign={onReassign}
              onPersonClick={onPersonClick}
            />
          ))}
        </Box>

        {hasMore && (
          <Box className="dashboard-attention-footer">
            <Button
              variant="text"
              className="dashboard-attention-show-more"
              onClick={() => {
                setShowAll(!showAll);
                if (onShowMore && !showAll) onShowMore();
              }}
            >
              {showAll ? "Show fewer items ↑" : "Show more items →"}
            </Button>
          </Box>
        )}
      </Box>
    </section>
  );
};

export default memo(DashboardAttentionAlerts);
